import { useEffect, useState, useMemo } from 'react';
import { useIsMounted } from 'usehooks-ts';

const loadedImages = new Set<string>();
const MAX_CACHE_SIZE = 1000;

export const usePreloadImages = (imageSources: string[]) => {
  const newSources = useMemo(
    () => imageSources.filter((src) => !loadedImages.has(src)),
    [imageSources]
  );
  const [isLoading, setIsLoading] = useState(newSources.length > 0);
  const isMounted = useIsMounted();

  useEffect(() => {
    if (newSources.length === 0) {
      return;
    }

    const imagePromises = newSources.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => {
            if (loadedImages.size >= MAX_CACHE_SIZE) {
              const firstEntry = loadedImages.values().next().value;
              if (firstEntry) {
                loadedImages.delete(firstEntry);
              }
            }
            loadedImages.add(src);
            resolve();
          };
          img.onerror = () => resolve();
        })
    );

    Promise.all(imagePromises).then(() => {
      if (isMounted()) {
        setIsLoading(false);
      }
    });
  }, [newSources, isMounted]);

  return isLoading;
};
